import axios from 'axios';

const REFRESH_ENDPOINT = '/v1/api/auth/refresh_jwt';
const SESSION_REFRESH_ENDPOINT = '/v1/api/auth/generate_jwt';
const REFRESH_MARGIN_MS = 5 * 60 * 1000; // refresh 5 minutes before expiry
const SESSION_INIT_MAX_ATTEMPTS = 3;
const SESSION_INIT_RETRY_DELAY_MS = 1000;
const SESSION_REFRESH_TIMEOUT_MS = 8000;
const SESSION_REFRESH_CONFIG = {
  withCredentials: true,
  timeout: SESSION_REFRESH_TIMEOUT_MS,
} as const;

// Access token lives in memory only — never persisted to localStorage or cookies.
// On page refresh the token is gone; the app calls the session-based refresh
// endpoint (authenticated via the HttpOnly Django session cookie) to obtain
// a fresh access token.
let token: string | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
// Shared promise for the in-flight session refresh. Concurrent callers
// (React StrictMode double-mount, multiple components calling on mount)
// must await the same network request — otherwise the second caller
// resolves `false` while the first is still in flight and AuthGate
// flips to the unauth tree even though the session is valid.
let initializing: Promise<boolean> | null = null;
// Coalesces concurrent force-refresh callers (socket reconnect storms, several
// recovery events firing at once) onto a single network roundtrip.
let refreshing: Promise<string | null> | null = null;

/**
 * Decode the payload of a JWT without verifying the signature.
 */
function decodeJwtPayload(jwt: string): { exp?: number } {
  try {
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      return {};
    }
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = atob(base64);
    return JSON.parse(jsonStr);
  } catch {
    return {};
  }
}

function scheduleRefresh(): void {
  clearRefreshTimer();

  if (!token) {
    return;
  }

  const payload = decodeJwtPayload(token);
  if (!payload.exp) {
    return;
  }

  const expiresAtMs = payload.exp * 1000;
  const now = Date.now();
  const refreshAtMs = expiresAtMs - REFRESH_MARGIN_MS;
  const delay = Math.max(refreshAtMs - now, 0);

  refreshTimer = setTimeout(() => {
    void refreshToken();
  }, delay);
}

function clearRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

// --- Public API ---

export function getToken(): string | null {
  return token;
}

export function setToken(jwt: string): boolean {
  // Reject tokens with no decodable expiry or an expiry already in the past:
  // a malformed / stale URL fragment must not pollute the in-memory store and
  // trigger a doomed bootstrap roundtrip.
  const payload = decodeJwtPayload(jwt);
  if (!payload.exp || payload.exp * 1000 <= Date.now()) {
    // eslint-disable-next-line no-console
    console.warn('authStore.setToken: rejecting JWT with missing or expired exp');
    return false;
  }
  token = jwt;
  scheduleRefresh();
  return true;
}

export function clearToken(): void {
  token = null;
  clearRefreshTimer();
}

// Literal route segments that sit at the same depth as `:scenario` under
// `/i/`. They must NOT be treated as scenario_ids, otherwise BootstrapProvider
// fires a bootstrap with `scenario-id: accept-invitation` and the backend
// 404s on `ChatImplementation.objects.get(scenario_id="accept-invitation")`,
// leaving the user stuck on the bootstrap-error screen instead of reaching
// the actual route component.
const NON_SCENARIO_I_PATHS: ReadonlySet<string> = new Set(['accept-invitation']);

/**
 * Extract scenario_id from the URL path. Expected pattern: `/i/{scenarioId}/...`.
 * Returns an empty string outside operator-app routes (e.g. login, auth
 * callback) and when the first `/i/` segment is a literal route name rather
 * than a real scenario_id.
 */
export function getScenarioId(): string {
  const match = window.location.pathname.match(/^\/i\/([^/]+)/);
  if (!match) return '';
  if (NON_SCENARIO_I_PATHS.has(match[1])) return '';
  return match[1];
}

export function isAuthenticated(): boolean {
  return token !== null;
}

/**
 * Called on app startup. If no in-memory token exists, attempts to obtain one
 * via the session-based refresh endpoint (which uses the HttpOnly Django
 * session cookie for authentication). This handles page refreshes where
 * the in-memory token is lost but the user still has an active session.
 *
 * Returns true if a token was obtained, false otherwise.
 */
export async function initializeFromSession(): Promise<boolean> {
  // If we already have a token (e.g. from login response), nothing to do
  if (token) {
    return true;
  }

  // Concurrent callers share the same in-flight refresh. Returning the
  // existing promise lets every caller resolve on the real outcome
  // (true or false) instead of the second caller getting a definitive
  // `false` while the first is still waiting on the network — which
  // would let AuthGate render the unauth tree before the token lands.
  if (initializing) {
    return initializing;
  }

  initializing = (async (): Promise<boolean> => {
    try {
      for (let attempt = 1; attempt <= SESSION_INIT_MAX_ATTEMPTS; attempt++) {
        try {
          const response = await axios.get<{ token: string }>(
            SESSION_REFRESH_ENDPOINT,
            SESSION_REFRESH_CONFIG,
          );
          setToken(response.data.token);
          return true;
        } catch (err) {
          // Only a definitive 401/403 means "no session — show login". A network
          // error or 5xx (VPN reconnect, laptop wake, cold backend instance) must
          // not bounce a user with a valid session cookie to the login screen.
          if (isAuthError(err)) {
            return false;
          }
          if (attempt < SESSION_INIT_MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, SESSION_INIT_RETRY_DELAY_MS));
          }
        }
      }
      return false;
    } finally {
      initializing = null;
    }
  })();
  return initializing;
}

function isAuthError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
}

/**
 * Force-refresh the access token regardless of current state. Unlike
 * initializeFromSession (which no-ops when a token is already in memory),
 * this always hits the network — needed when a background tab's scheduleRefresh
 * timer froze and the socket wakes with an expired-but-present token. Mirrors
 * the httpClient 401 interceptor's Bearer-refresh -> session-fallback chain.
 * Returns the new token, or null on failure.
 */
export async function refreshToken(): Promise<string | null> {
  if (refreshing) {
    return refreshing;
  }

  refreshing = (async (): Promise<string | null> => {
    const currentToken = token;
    if (currentToken) {
      try {
        const response = await axios.post<{ token: string }>(REFRESH_ENDPOINT, null, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        setToken(response.data.token);
        return response.data.token;
      } catch (err) {
        if (!isAuthError(err)) {
          return null;
        }
      }
    }

    try {
      const response = await axios.get<{ token: string }>(
        SESSION_REFRESH_ENDPOINT,
        SESSION_REFRESH_CONFIG,
      );
      setToken(response.data.token);
      return response.data.token;
    } catch (err) {
      if (isAuthError(err)) {
        clearToken();
      }
      return null;
    }
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}
